import React from 'react';
import { Shield, Printer } from 'lucide-react';
import { CONTACT_EMAILS } from '../constants/contact';

/**
 * PrivacyPolicy - Privacy Policy page for GDPR/CCPA compliance (Issue #1310)
 *
 * Discloses:
 * - Cookie usage (Google Analytics, Sentry, Stripe, CSRF)
 * - Data collection practices
 * - Third-party services
 * - User rights (access, deletion, opt-out)
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-secondary" />
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 text-secondary hover:text-primary-dark hover:bg-secondary-light/50 rounded-lg transition-colors print:hidden"
              aria-label="Print this document"
            >
              <Printer className="w-5 h-5" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-2">
            © {new Date().getFullYear()} Local Artist Fairs. All rights reserved.
          </p>
          <p className="text-sm text-gray-600 mb-8">
            Last Updated: March 1, 2026
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Local Artist Fairs ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using Local Artist Fairs, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you create an account or use our services, we may collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, business name</li>
              <li><strong>Profile Information:</strong> Vendor profiles, product catalogs, booth preferences</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store full payment details)</li>
              <li><strong>Communication Data:</strong> Messages, feedback, support requests</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
              <li><strong>Device Information:</strong> Browser type, IP address (anonymized), device type</li>
              <li><strong>Analytics Data:</strong> Collected via Google Analytics (see Cookie Policy)</li>
              <li><strong>Error Logs:</strong> Technical errors for debugging (via Sentry)</li>
            </ul>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track activity on our platform and store certain information.
              For detailed information about our cookie usage, please see our{' '}
              <a href="/cookies" className="text-secondary hover:text-primary-dark underline">Cookie Policy</a>.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Types of Cookies We Use</h3>
            <div className="space-y-4 mb-4">
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-800">Essential Cookies (No consent required)</h4>
                <p className="text-gray-700">CSRF protection, session management, security</p>
              </div>
              <div className="border-l-4 border-dusty-teal pl-4">
                <h4 className="font-semibold text-gray-800">Analytics Cookies (Requires consent)</h4>
                <p className="text-gray-700">Google Analytics (GA4) for usage analytics</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-semibold text-gray-800">Functional Cookies</h4>
                <p className="text-gray-700">Error tracking (Sentry), payment processing (Stripe)</p>
              </div>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the following third-party services that may collect information about you:
            </p>

            <div className="space-y-4 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Google Analytics (GA4)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Purpose:</strong> Usage analytics, user behavior tracking
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Data Collected:</strong> Page views, events, anonymized user IDs
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Privacy Policy:</strong>{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                    Google Privacy Policy
                  </a>
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Sentry</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Purpose:</strong> Error monitoring, session replay
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Data Collected:</strong> Error logs, stack traces, session information
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Privacy Policy:</strong>{' '}
                  <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                    Sentry Privacy Policy
                  </a>
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Stripe</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Purpose:</strong> Payment processing, fraud prevention
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Data Collected:</strong> Payment information, transaction details
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Privacy Policy:</strong>{' '}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                    Stripe Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>Provide and maintain our platform services</li>
              <li>Process vendor applications, booth reservations, and payments</li>
              <li>Send notifications about fair events, slot offers, and waitlists</li>
              <li>Improve user experience and platform functionality</li>
              <li>Detect and prevent fraud, security threats, and technical issues</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
              <li>Communicate with you about updates, support, and promotional content</li>
            </ul>
          </section>

          {/* Data Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Fair Managers:</strong> Vendor profiles are visible to fair managers for event coordination</li>
              <li><strong>Service Providers:</strong> Third-party services (Google Analytics, Sentry, Stripe) as described above</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>
          </section>

          {/* Your Privacy Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">GDPR Rights (European Users)</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are located in the European Economic Area (EEA), you have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data by contacting our privacy team</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Right to Object:</strong> Opt out of analytics and marketing communications</li>
              <li><strong>Right to Withdraw Consent:</strong> Change your cookie preferences at any time</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">CCPA Rights (California Users)</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Right to Know:</strong> Request disclosure of collected personal information</li>
              <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Right to Opt-Out:</strong> Opt out of the "sale" of personal information (we do not sell data)</li>
              <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise your rights, please visit your{' '}
              <a href="/profile" className="text-secondary hover:text-primary-dark underline">user profile settings</a>
              {' '}or contact us at <a href={`mailto:${CONTACT_EMAILS.privacy}`} className="text-secondary hover:text-primary-dark underline">{CONTACT_EMAILS.privacy}</a>.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy,
              unless a longer retention period is required or permitted by law.
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Transaction Records:</strong> Retained for 7 years for tax and legal compliance</li>
              <li><strong>Analytics Data:</strong> Google Analytics retains data for 26 months</li>
              <li><strong>Deleted Accounts:</strong> Personal data deleted within 30 days of account closure</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-4">
              <li>HTTPS encryption for all data transmission</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>CSRF protection for all state-changing requests</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication for sensitive data</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              However, no method of transmission over the Internet is 100% secure. While we strive to protect your data,
              we cannot guarantee absolute security.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our platform is not intended for users under the age of 18. We do not knowingly collect personal information
              from children under 18. If you are a parent or guardian and believe your child has provided us with personal
              information, please contact us, and we will delete such information.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You are advised to review this Privacy Policy periodically for any changes. Changes are effective when posted.
            </p>
          </section>

          {/* Contact Us */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2">
                <strong>Email:</strong>{' '}
                <a href={`mailto:${CONTACT_EMAILS.privacy}`} className="text-secondary hover:text-primary-dark underline">
                  {CONTACT_EMAILS.privacy}
                </a>
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Feedback Form:</strong>{' '}
                <a href="/feedback" className="text-secondary hover:text-primary-dark underline">
                  Submit Feedback
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong>{' '}
                <a href="https://localartistfairs.com" target="_blank" rel="noopener noreferrer" className="text-secondary hover:text-primary-dark underline">
                  https://localartistfairs.com
                </a>
              </p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <a href="/cookies" className="hover:text-secondary underline">Cookie Policy</a>
              <span>|</span>
              <a href="/terms" className="hover:text-secondary underline">Terms of Service</a>
              <span>|</span>
              <a href="/" className="hover:text-secondary underline">Back to Home</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
